import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { updateSEOMeta, injectJSONLD } from '@/components/base/SEOHead';
import { categories, categoryMap } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = slug ? categoryMap[slug] : undefined;
  const [activeSlug, setActiveSlug] = useState(slug || 'rumen');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  const currentCat = categoryMap[activeSlug];
  const categoryArticles = getArticlesByCategory(activeSlug);

  useEffect(() => {
    if (currentCat) {
      updateSEOMeta({
        title: `${currentCat.name} - 抖ying${currentCat.name}完整教程 | 抖ying指南`,
        description: `${currentCat.description}，共收录${currentCat.articleCount}篇专业文章，助你全面掌握抖ying${currentCat.name}核心方法。`,
        keywords: `抖ying${currentCat.name},抖ying教程,抖ying${currentCat.name}技巧`,
        canonicalUrl: `https://douyin-guide.com/category/${activeSlug}`,
      });

      injectJSONLD({
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: `抖ying${currentCat.name}教程合集`,
        description: currentCat.description,
        url: `https://douyin-guide.com/category/${activeSlug}`,
      }, 'schema-collection');
    }
  }, [currentCat, activeSlug]);

  return (
    <div className="min-h-screen bg-background-50">
      <ScrollToTop />
      <Navbar />

      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: currentCat?.name || '分类' },
              ]}
            />
          </div>

          {/* Category Header */}
          <div className="mb-10">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-3">
              {currentCat?.name}
            </h1>
            <p className="text-foreground-600 text-sm md:text-base">
              {currentCat?.description}
            </p>
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar */}
            <aside className="lg:w-64 flex-shrink-0">
              <div className="lg:sticky lg:top-24">
                <h3 className="text-sm font-semibold text-foreground-700 mb-3 uppercase tracking-wider">
                  内容分类
                </h3>
                <nav className="space-y-1">
                  {categories.map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.slug}`}
                      onClick={() => setActiveSlug(cat.slug)}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors no-underline ${
                        activeSlug === cat.slug
                          ? 'bg-primary-100/60 text-primary-700 font-medium'
                          : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
                      }`}
                    >
                      <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${
                        activeSlug === cat.slug ? 'bg-primary-200 text-primary-700' : 'bg-background-100 text-foreground-500'
                      }`}>
                        <i className={`${cat.icon} text-sm`}></i>
                      </div>
                      <span className="whitespace-nowrap">{cat.name}</span>
                      <span className="ml-auto text-xs text-foreground-400">{cat.articleCount}</span>
                    </Link>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Article List */}
            <div className="flex-1 min-w-0">
              {categoryArticles.length === 0 ? (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100">
                    <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500">该分类暂无文章</p>
                </div>
              ) : (
                <div className="space-y-5">
                  {categoryArticles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.slug}`}
                      className="group flex flex-col sm:flex-row gap-5 p-5 bg-background-50 border border-background-200/70 rounded-xl hover:border-primary-200 transition-all duration-300 no-underline"
                    >
                      <div className="sm:w-56 flex-shrink-0 h-40 sm:h-auto rounded-lg overflow-hidden">
                        <img
                          src={article.imageUrl}
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="flex-1 min-w-0 flex flex-col">
                        <h2 className="font-heading text-lg font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                          {article.title}
                        </h2>
                        <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-3 flex-1">
                          {article.excerpt}
                        </p>
                        <div className="flex flex-wrap items-center gap-2 mb-3">
                          {article.tags.slice(0, 3).map((tag) => (
                            <span
                              key={tag}
                              className="px-2 py-0.5 bg-secondary-100 text-secondary-700 text-xs rounded-md whitespace-nowrap"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                        <div className="flex items-center gap-4 text-xs text-foreground-400">
                          <span className="flex items-center gap-1">
                            <i className="ri-time-line"></i>
                            {article.readTime}
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="ri-calendar-line"></i>
                            {article.date}
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="ri-user-line"></i>
                            {article.author}
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}